export type CellValue = 'yes' | 'no' | 'partial' | string;

export interface ComparisonRow {
  feature: string;
  description?: string;
  lb: CellValue;
  them: CellValue;
}

export interface CompetitorData {
  name: string;
  slug: string;
  category: string;
  tagline: string;
  metaTitle: string;
  metaDescription: string;
  heroTitle: string;
  heroDescription: string;
  theirStrength: string;
  lbStrength: string;
  pricing: { lb: string; them: string };
  timeToList: { lb: string; them: string };
  rows: ComparisonRow[];
  theirPros: string[];
  lbWins: string[];
  verdict: string;
}

const baseRows = (overrides: Partial<Record<string, { lb?: CellValue; them?: CellValue }>> = {}): ComparisonRow[] => {
  const defaults: ComparisonRow[] = [
    { feature: 'AI photo enhancement', description: 'HDR, lighting, and colour correction', lb: 'yes', them: 'no' },
    { feature: 'Virtual staging', description: 'Furnish empty rooms with AI', lb: 'yes', them: 'no' },
    { feature: 'Sky & lawn replacement', description: 'Fix overcast skies and patchy lawns', lb: 'yes', them: 'no' },
    { feature: 'Object & clutter removal', description: 'Clean up distracting items automatically', lb: 'yes', them: 'no' },
    { feature: 'AI listing descriptions', description: 'Persuasive, SEO-ready property copy', lb: 'yes', them: 'no' },
    { feature: 'Multi-language copy', description: 'Listings in 28+ languages instantly', lb: 'yes', them: 'no' },
    { feature: 'Social media posts', description: 'Ready-to-post Facebook & Instagram content', lb: 'yes', them: 'no' },
    { feature: 'Property slideshow video', description: 'Auto-generated video with music & transitions', lb: 'yes', them: 'no' },
    { feature: 'Print-ready PDF flyer', description: 'Branded marketing flyer with QR code', lb: 'yes', them: 'no' },
    { feature: 'Shareable property microsite', description: 'A dedicated web page per listing', lb: 'yes', them: 'no' },
    { feature: 'Import from listing URL', description: 'Paste a URL to auto-import photos & details', lb: 'yes', them: 'no' },
    { feature: 'No special hardware needed', description: 'Works with any phone or DSLR', lb: 'yes', them: 'yes' },
    { feature: 'Complete workflow in one platform', description: 'Photos → copy → distribution in one place', lb: 'yes', them: 'no' },
  ];
  return defaults.map(row => {
    const override = overrides[row.feature];
    if (override) {
      return { ...row, lb: override.lb ?? row.lb, them: override.them ?? row.them };
    }
    return row;
  });
};

export const competitors: CompetitorData[] = [
  {
    name: '3DVista',
    slug: '3dvista',
    category: '360° Virtual Tour Software',
    tagline: 'Interactive 360° virtual tour builder with hotspots and VR support',
    metaTitle: 'ListBright vs 3DVista – Full Listing Platform vs Virtual Tour Tool (2025)',
    metaDescription: 'ListBright vs 3DVista: compare AI-powered listing creation against 360° virtual tour software. See which is right for your real estate workflow.',
    heroTitle: 'ListBright vs 3DVista',
    heroDescription:
      '3DVista is a powerful tool for building interactive 360° virtual tours. ListBright is an end-to-end AI platform that creates, writes, and distributes the entire property listing — no special camera required.',
    theirStrength:
      '3DVista excels at creating immersive, interactive 360° virtual tours complete with hotspots, floor plan overlays, and VR headset compatibility — the premium choice when you own a 360° camera and want a guided walkthrough experience.',
    lbStrength:
      'ListBright handles everything from AI photo enhancement and virtual staging to copywriting and multi-channel distribution — all from ordinary smartphone or DSLR photos, finished in about 10 minutes.',
    pricing: { lb: 'From $15 / listing', them: '~$399 / year + 360° camera ($500–$3,500)' },
    timeToList: { lb: '~10 min', them: '2–4 hrs (shoot + software)' },
    rows: [
      ...baseRows({
        'No special hardware needed': { lb: 'yes', them: 'no' },
      }),
      { feature: '360° interactive tours', description: 'Immersive guided property walkthroughs', lb: 'no', them: 'yes' },
      { feature: 'VR headset support', description: 'View tours in virtual reality', lb: 'no', them: 'yes' },
    ],
    theirPros: [
      'Industry-leading interactive 360° tour quality',
      'Hotspot overlays, floor plan links, and VR headset support',
      'Strong brand recognition in the virtual tour space',
    ],
    lbWins: [
      'No 360° camera purchase or rental required — any phone works',
      'AI-written listing descriptions in 28+ languages',
      'Complete listing — photos, copy, video, flyer, microsite — in ~10 min',
      'Far lower cost per listing; scales across your entire portfolio',
    ],
    verdict:
      '3DVista is the right specialist tool for agents who already own a 360° camera and need premium immersive tours. ListBright is the right choice for agents who want a full, professional listing — enhanced photos, compelling copy, and every marketing asset — produced in 10 minutes with no hardware investment.',
  },

  {
    name: 'AI HomeDesign',
    slug: 'ai-homedesign',
    category: 'AI Interior Design & Staging',
    tagline: 'AI-powered room redesign and interior visualization tool',
    metaTitle: 'ListBright vs AI HomeDesign – Complete Listing Platform vs Design Tool (2025)',
    metaDescription: 'Compare ListBright and AI HomeDesign. One handles your entire listing workflow; the other focuses on interior redesign visualization. See the difference.',
    heroTitle: 'ListBright vs AI HomeDesign',
    heroDescription:
      'AI HomeDesign reimagines rooms in different styles with AI. ListBright takes it further — enhancing your actual photos, writing compelling copy, and producing every marketing asset your listing needs.',
    theirStrength:
      'AI HomeDesign offers a wide range of interior design styles for room visualization, letting buyers see spaces reimagined in dozens of aesthetics — useful for inspiration and buyer engagement.',
    lbStrength:
      'ListBright is purpose-built for real estate: it enhances your existing photos professionally, furnishes vacant rooms, writes SEO-ready descriptions in 28+ languages, and delivers video, flyers, and a microsite — all from a single upload.',
    pricing: { lb: 'From $15 / listing', them: 'Credits-based (~$29+/mo for limited renders)' },
    timeToList: { lb: '~10 min', them: 'Photos only — no listing copy or assets' },
    rows: baseRows({
      'AI photo enhancement': { lb: 'yes', them: 'no' },
      'Virtual staging': { lb: 'yes', them: 'partial' },
      'Sky & lawn replacement': { lb: 'yes', them: 'no' },
    }),
    theirPros: [
      'Wide variety of interior design and architectural styles',
      'Good for mood-board and buyer inspiration content',
      'Simple drag-and-drop interface',
    ],
    lbWins: [
      'Enhances your actual listing photos — not just room redesigns',
      'Generates SEO-ready copy and multi-language descriptions automatically',
      'Produces video, PDF flyer, and shareable microsite in one workflow',
      'Designed specifically for real estate agents, not general design users',
    ],
    verdict:
      'AI HomeDesign is a creative tool for visualizing different interior styles. ListBright is a complete real estate listing platform — if you need one tool to take a property from raw photos to a published, multi-channel listing, ListBright delivers everything AI HomeDesign doesn\'t.',
  },

  {
    name: 'Apply Design',
    slug: 'apply-design',
    category: 'AI Virtual Staging',
    tagline: 'AI virtual staging with photorealistic furnished room renders',
    metaTitle: 'ListBright vs Apply Design – Full Listing Platform vs Staging Tool (2025)',
    metaDescription: 'ListBright vs Apply Design: compare a complete AI listing platform against a virtual staging tool. See which delivers more value per listing.',
    heroTitle: 'ListBright vs Apply Design',
    heroDescription:
      'Apply Design creates photorealistic staged versions of empty rooms. ListBright does that and everything else — photo enhancement, copywriting, video, flyers, and multi-language distribution.',
    theirStrength:
      'Apply Design produces high-quality AI-staged room renders with a focus on photorealism, offering a solid selection of furniture styles for vacant room presentation.',
    lbStrength:
      'ListBright is a full listing platform: virtual staging is just one of its many AI tools. It also enhances every photo, writes the listing description, and produces all your marketing assets automatically.',
    pricing: { lb: 'From $15 / listing', them: 'Credits-based (pay per staged image)' },
    timeToList: { lb: '~10 min', them: 'Staged photos only — no copy, video, or flyer' },
    rows: baseRows({
      'Virtual staging': { lb: 'yes', them: 'yes' },
    }),
    theirPros: [
      'Photorealistic virtual staging renders',
      'Multiple furniture style options',
      'Simple upload-and-stage workflow',
    ],
    lbWins: [
      'Virtual staging plus full photo enhancement in one workflow',
      'AI listing descriptions and multi-language copy included',
      'Auto-generates video, PDF flyer, social posts, and microsite',
      'One platform covers every marketing asset — not just staged photos',
    ],
    verdict:
      'Apply Design does one thing well: staged room renders. ListBright does staging and everything else a real estate agent needs to market a property. For agents who want to produce a complete, publish-ready listing without juggling multiple tools, ListBright is the clear choice.',
  },

  {
    name: 'BoxBrownie',
    slug: 'boxbrownie',
    category: 'Real Estate Photo Editing',
    tagline: 'Human-edited photo retouching, virtual staging, and floor plans for real estate',
    metaTitle: 'ListBright vs BoxBrownie – AI Platform vs Human Photo Editing Service (2025)',
    metaDescription: 'Compare ListBright and BoxBrownie for real estate photo editing and listing creation. AI speed vs human-touch quality — see which fits your workflow.',
    heroTitle: 'ListBright vs BoxBrownie',
    heroDescription:
      'BoxBrownie uses skilled human editors to retouch real estate photos. ListBright uses AI to enhance photos and then goes further — writing the listing copy, generating videos, and producing every marketing asset automatically.',
    theirStrength:
      "BoxBrownie's human editing team delivers meticulous photo retouching with a personal review process — excellent for high-end properties where pixel-perfect editing precision matters more than turnaround speed.",
    lbStrength:
      'ListBright delivers AI-quality photo enhancement in seconds — not hours — and then produces the full listing: copy in 28+ languages, slideshow video, PDF flyer, social posts, and a shareable microsite, all from one upload.',
    pricing: { lb: 'From $15 / listing (all-in)', them: '$1.60–$9+ per image (editing only)' },
    timeToList: { lb: '~10 min', them: '24–48 hr turnaround for edited photos only' },
    rows: baseRows({
      'AI photo enhancement': { lb: 'yes', them: 'partial' },
      'Virtual staging': { lb: 'yes', them: 'yes' },
      'Sky & lawn replacement': { lb: 'yes', them: 'yes' },
      'Object & clutter removal': { lb: 'yes', them: 'yes' },
      'No special hardware needed': { lb: 'yes', them: 'yes' },
    }),
    theirPros: [
      'Human editors produce meticulous, pixel-perfect results',
      'Wide range of services: HDR, twilight, floor plans, object removal',
      'Well-established reputation in the real estate industry',
    ],
    lbWins: [
      'AI delivers enhanced photos instantly — no 24–48 hr wait',
      'Generates listing descriptions and multilingual copy automatically',
      'Produces video, flyer, social posts, and microsite — not just photos',
      'Significantly lower all-in cost per listing',
    ],
    verdict:
      'BoxBrownie is a trusted photo editing service for agents who want human oversight on their images and can wait up to 48 hours. ListBright is for agents who need a complete, publish-ready listing fast — AI-enhanced photos plus all the copy and marketing assets, delivered in minutes for a fraction of the price.',
  },

  {
    name: 'CloudPano',
    slug: 'cloudpano',
    category: '360° Virtual Tour Platform',
    tagline: 'Create, host, and share 360° virtual tours for real estate and business',
    metaTitle: 'ListBright vs CloudPano – Full Listing Platform vs 360° Tour Software (2025)',
    metaDescription: 'Compare ListBright and CloudPano for real estate marketing. AI listing creation vs 360° virtual tour hosting — see which covers your workflow.',
    heroTitle: 'ListBright vs CloudPano',
    heroDescription:
      'CloudPano makes it easy to build and host 360° virtual tours. ListBright takes a different approach — enhancing standard photos with AI, writing the copy, and producing every listing asset without any special camera.',
    theirStrength:
      'CloudPano offers fast and affordable 360° virtual tour creation with hosting, embed codes, and lead capture — a practical choice when you already shoot with a 360° camera.',
    lbStrength:
      'ListBright works with photos from any camera, enhancing them with AI and automatically producing a complete listing: descriptions in 28+ languages, slideshow video, PDF flyer, social posts, and a shareable microsite.',
    pricing: { lb: 'From $15 / listing', them: '~$29–$65 / month + 360° camera required' },
    timeToList: { lb: '~10 min', them: '1–2 hrs (shoot + tour build); no copy or flyer' },
    rows: [
      ...baseRows({
        'No special hardware needed': { lb: 'yes', them: 'no' },
      }),
      { feature: '360° virtual tour hosting', description: 'Host and share interactive 360° tours', lb: 'no', them: 'yes' },
    ],
    theirPros: [
      'User-friendly 360° tour builder with floor plan integration',
      'Reliable hosting with embed codes for portals and websites',
      'Lead capture and analytics built into tours',
    ],
    lbWins: [
      'No 360° camera purchase required — any smartphone works',
      'AI photo enhancement, virtual staging, and copy generation included',
      'Complete listing assets: video, flyer, social posts, microsite',
      'Designed around the entire listing workflow, not just tours',
    ],
    verdict:
      'CloudPano is a solid 360° tour platform for agents already shooting with a 360° camera. ListBright is the better choice for agents who want a full, professional listing produced from standard photos — enhanced, described, and distributed — in 10 minutes flat.',
  },

  {
    name: 'Curated Social',
    slug: 'curated-social',
    category: 'Real Estate Social Media Content',
    tagline: 'Social media content templates and scheduling for real estate agents',
    metaTitle: 'ListBright vs Curated Social – All-in-One Listing Platform vs Social Tool (2025)',
    metaDescription: 'ListBright vs Curated Social: compare a complete AI listing platform against a real estate social media content tool. See which drives more results.',
    heroTitle: 'ListBright vs Curated Social',
    heroDescription:
      'Curated Social helps real estate agents create and schedule social media content. ListBright builds the entire listing — enhanced photos, copy, and social posts — then delivers ready-to-share assets all at once.',
    theirStrength:
      'Curated Social provides a library of real estate social media templates, captions, and a scheduling tool, making it easy for agents to maintain a consistent posting calendar without starting from scratch.',
    lbStrength:
      'ListBright generates property-specific social media posts automatically, alongside the full listing: AI-enhanced photos, compelling descriptions in 28+ languages, a slideshow video, and a branded PDF flyer.',
    pricing: { lb: 'From $15 / listing', them: 'Subscription (~$49+/mo)' },
    timeToList: { lb: '~10 min', them: 'Social posts only — photos and copy still need separate tools' },
    rows: baseRows({
      'Social media posts': { lb: 'yes', them: 'yes' },
    }),
    theirPros: [
      'Ready-made social media content calendar for consistent posting',
      'Templates for market updates, testimonials, and agent branding',
      'Scheduling integration with Facebook, Instagram, and more',
    ],
    lbWins: [
      'Social posts are property-specific and auto-generated from your listing',
      'Includes AI photo enhancement, staging, and listing copy in one workflow',
      'Produces video, PDF flyer, and microsite alongside social content',
      'No separate copywriting, design, or scheduling tool needed',
    ],
    verdict:
      'Curated Social is a useful tool for agents building a general social media presence. ListBright is the right choice when you want property-specific, publication-ready content — enhanced photos, descriptions, and social posts all generated from a single listing upload.',
  },

  {
    name: 'Matterport',
    slug: 'matterport',
    category: '3D Property Scanning',
    tagline: 'Industry-standard 3D digital twin and spatial scanning for real estate',
    metaTitle: 'ListBright vs Matterport – AI Listing Platform vs 3D Property Scanner (2025)',
    metaDescription: 'ListBright vs Matterport: AI-generated listings vs 3D digital twin scanning. Compare cost, features, and which is right for your listings.',
    heroTitle: 'ListBright vs Matterport',
    heroDescription:
      'Matterport creates extraordinary 3D digital twins of properties. ListBright creates extraordinary listings — AI-enhanced photos, compelling multi-language copy, and all your marketing assets — using the camera already in your pocket.',
    theirStrength:
      'Matterport produces best-in-class 3D spatial scans with a unique dollhouse view, accurate measurements, and the industry-leading standard for luxury and commercial property tours.',
    lbStrength:
      'ListBright requires no hardware investment: upload any photos, and AI enhances them, writes the copy, and produces videos, flyers, and multilingual listings in about 10 minutes — for a fraction of Matterport\'s cost.',
    pricing: { lb: 'From $15 / listing', them: '$34–$309/mo + Matterport camera ($400–$3,500)' },
    timeToList: { lb: '~10 min', them: '2–4 hrs (scan + processing); no copy or flyer' },
    rows: [
      ...baseRows({
        'No special hardware needed': { lb: 'yes', them: 'no' },
      }),
      { feature: '3D spatial scan (dollhouse view)', description: 'True 3D model with measurement tools', lb: 'no', them: 'yes' },
      { feature: 'Floor plan extraction', description: 'Auto-generate accurate floor plans from scan', lb: 'no', them: 'yes' },
    ],
    theirPros: [
      'Best-in-class immersive 3D property tours with dollhouse and floor plan views',
      'Accurate spatial measurements — useful for commercial and premium residential',
      'Industry-recognised brand trusted by top agencies worldwide',
    ],
    lbWins: [
      'Zero hardware cost — works with any phone or existing photography',
      'AI-written listing descriptions and multilingual copy included',
      'Full marketing suite: video, flyer, social posts, and microsite in one go',
      'Per-listing cost is dramatically lower — ideal for mid-market volume',
    ],
    verdict:
      'Matterport is the gold standard for luxury and commercial listings that demand immersive 3D tours and spatial accuracy. ListBright is the smarter choice for the vast majority of residential listings: no camera investment, a complete set of marketing assets, and 10-minute turnaround per property.',
  },

  {
    name: 'Pedra',
    slug: 'pedra',
    category: 'AI Virtual Staging',
    tagline: 'Fast AI virtual staging and renovation visualization for real estate',
    metaTitle: 'ListBright vs Pedra – Complete Listing Platform vs AI Staging Tool (2025)',
    metaDescription: 'Compare ListBright and Pedra for real estate AI tools. Full listing platform vs AI virtual staging — see which delivers more value per property.',
    heroTitle: 'ListBright vs Pedra',
    heroDescription:
      'Pedra is a quick and accessible AI staging tool. ListBright is a complete listing platform — it stages vacant rooms, enhances every photo, and also writes the copy, generates the video, and delivers all your marketing assets.',
    theirStrength:
      'Pedra offers fast AI virtual staging and renovation rendering with an intuitive interface and a growing library of furniture styles — a popular entry-level staging tool in the European market.',
    lbStrength:
      'ListBright combines AI staging with photo enhancement, sky replacement, and clutter removal, then automatically generates SEO-ready listing descriptions in 28+ languages plus a video, flyer, and shareable microsite.',
    pricing: { lb: 'From $15 / listing', them: '€29–€59/mo (credits-based, staging only)' },
    timeToList: { lb: '~10 min', them: 'Staged images only — no copy, video, or flyer' },
    rows: baseRows({
      'Virtual staging': { lb: 'yes', them: 'yes' },
    }),
    theirPros: [
      'Fast, accessible AI staging with a clean interface',
      'Renovation and empty-to-furnished visualization in seconds',
      'Affordable entry-level pricing for staging only',
    ],
    lbWins: [
      'Staging plus full photo enhancement in the same workflow',
      'AI listing descriptions in 28+ languages generated automatically',
      'Produces video, PDF flyer, social posts, and microsite alongside photos',
      'One platform handles everything — no separate copywriting or editing tools',
    ],
    verdict:
      'Pedra is a capable AI staging tool, especially for agents who only need staged renders. ListBright is the better choice when you need the complete listing package — staged and enhanced photos, professional copy, and all marketing assets — without stitching together multiple tools.',
  },

  {
    name: 'Realync',
    slug: 'realync',
    category: 'Real Estate Video Platform',
    tagline: 'Live and pre-recorded property video tours for real estate agents',
    metaTitle: 'ListBright vs Realync – AI Listing Platform vs Property Video Tours (2025)',
    metaDescription: 'Compare ListBright and Realync for real estate marketing. AI-generated listings vs live and recorded property video tours — see which fits your strategy.',
    heroTitle: 'ListBright vs Realync',
    heroDescription:
      'Realync connects agents with buyers through live and recorded video tours. ListBright connects buyers with properties through AI-enhanced photos, compelling copy, and auto-generated videos — all produced in 10 minutes without scheduling a call.',
    theirStrength:
      'Realync enables real-time video tours and 1:1 agent video messaging, creating a personal connection with remote buyers — valuable for markets with high out-of-area demand or luxury leasing.',
    lbStrength:
      'ListBright produces a full, professional listing automatically: enhanced photos, virtual staging, multilingual copy, a cinematic slideshow video, a PDF flyer, and a shareable microsite — ready to publish 24/7 without a live showing.',
    pricing: { lb: 'From $15 / listing', them: '~$49–$159/mo (video platform subscription)' },
    timeToList: { lb: '~10 min', them: 'Must schedule and record live tour; no copy or flyer' },
    rows: baseRows({
      'Property slideshow video': { lb: 'yes', them: 'partial' },
    }),
    theirPros: [
      'Real-time live video tours create personal buyer connections',
      'Recorded videos can be shared asynchronously after recording',
      'Good for luxury leasing and remote buyer markets',
    ],
    lbWins: [
      'No scheduling required — AI produces the full listing anytime',
      'AI photo enhancement, virtual staging, and sky replacement included',
      'Listing descriptions, social posts, flyer, and microsite all generated at once',
      'One upload replaces a photo shoot, copywriting session, and video call',
    ],
    verdict:
      'Realync is valuable for agents who want to offer live, personalised video tours to remote buyers. ListBright is the right tool for producing a complete, always-on property listing at scale — one that works for buyers at any time of day, in any language.',
  },

  {
    name: 'roOomy',
    slug: 'rooomy',
    category: 'AI Virtual Staging & 3D Visualization',
    tagline: 'High-end 3D virtual staging and design visualization for real estate',
    metaTitle: 'ListBright vs roOomy – Full Listing Platform vs Virtual Staging Service (2025)',
    metaDescription: 'Compare ListBright and roOomy for real estate marketing. End-to-end AI listing creation vs premium virtual staging and 3D visualization.',
    heroTitle: 'ListBright vs roOomy',
    heroDescription:
      'roOomy delivers detailed 3D virtual staging for high-end properties. ListBright delivers virtual staging plus the complete listing — AI-enhanced photos, professional copy in 28+ languages, video, flyer, and microsite — in one workflow.',
    theirStrength:
      'roOomy offers highly detailed 3D staging renders with premium furniture brands, making it a strong choice for luxury residential and commercial properties where design accuracy matters.',
    lbStrength:
      'ListBright is built for volume and completeness: AI staging comes bundled with photo enhancement, copywriting, and every marketing asset, at a price point that works across your whole portfolio.',
    pricing: { lb: 'From $15 / listing', them: 'Enterprise / per-room pricing (typically $60–$150+/room)' },
    timeToList: { lb: '~10 min', them: 'Staging renders only — no copy, video, or flyer' },
    rows: baseRows({
      'Virtual staging': { lb: 'yes', them: 'yes' },
    }),
    theirPros: [
      'Premium 3D staging with recognised furniture brands and fine detail',
      'Strong track record in luxury residential and commercial markets',
      'Product placement partnerships with real furniture retailers',
    ],
    lbWins: [
      'AI staging bundled with full photo enhancement at a fraction of the cost',
      'Generates listing copy in 28+ languages automatically',
      'Produces video, PDF flyer, social posts, and microsite in the same workflow',
      'Affordable enough to use on every listing — not just luxury properties',
    ],
    verdict:
      'roOomy is a premium 3D staging service best suited to luxury listings where furniture brand accuracy is essential. ListBright is the practical choice for agents across all price tiers — complete staging, enhancement, and listing production in 10 minutes at a price that scales.',
  },

  {
    name: 'Stager AI',
    slug: 'stager-ai',
    category: 'AI Virtual Staging',
    tagline: 'AI-powered virtual staging of vacant rooms in seconds',
    metaTitle: 'ListBright vs Stager AI – Complete Listing Platform vs AI Staging Tool (2025)',
    metaDescription: 'ListBright vs Stager AI: compare a complete real estate listing platform with a dedicated AI virtual staging tool. Features, pricing, and what you get.',
    heroTitle: 'ListBright vs Stager AI',
    heroDescription:
      'Stager AI furnishes vacant rooms in seconds using AI. ListBright does that and everything else — enhancing photos, writing compelling copy, and producing every marketing asset for your listing automatically.',
    theirStrength:
      'Stager AI offers a fast and user-friendly AI staging experience with multiple design style options, making it an accessible starting point for agents who need furnished room renders quickly.',
    lbStrength:
      'ListBright combines AI staging with photo enhancement, sky replacement, and clutter removal, then generates SEO-ready copy in 28+ languages, a cinematic video, a branded PDF flyer, and a shareable microsite.',
    pricing: { lb: 'From $15 / listing', them: '~$29–$99/mo (credits-based, staging only)' },
    timeToList: { lb: '~10 min', them: 'Staged images only — copy, video, and flyer not included' },
    rows: baseRows({
      'Virtual staging': { lb: 'yes', them: 'yes' },
    }),
    theirPros: [
      'Quick and intuitive AI staging interface',
      'Variety of interior design styles to choose from',
      'Accessible pricing for staging-only use cases',
    ],
    lbWins: [
      'Staging is one of many AI photo tools — all included in one workflow',
      'AI listing copy in 28+ languages generated from the same upload',
      'Video, PDF flyer, social posts, and microsite all produced automatically',
      'No need to switch between multiple tools for a complete listing',
    ],
    verdict:
      'Stager AI is a capable tool for agents who only need vacant-room staging. ListBright is the right choice for agents who need a complete property listing — staged and enhanced photos, professional copy, and all marketing materials — from a single upload.',
  },

  {
    name: 'Virtual Staging AI',
    slug: 'virtual-staging-ai',
    category: 'AI Virtual Staging',
    tagline: 'Fast AI virtual staging with multiple design styles, starting at under $1 per image',
    metaTitle: 'ListBright vs Virtual Staging AI – Full Platform vs Per-Image Staging (2025)',
    metaDescription: 'Compare ListBright and Virtual Staging AI for real estate. Complete listing creation vs affordable AI virtual staging — features, pricing, and value.',
    heroTitle: 'ListBright vs Virtual Staging AI',
    heroDescription:
      'Virtual Staging AI offers some of the fastest and most affordable AI-staged room renders available. ListBright includes AI staging alongside enhanced photos, listing copy, video, and every other asset a property listing needs.',
    theirStrength:
      'Virtual Staging AI delivers quick, affordable AI staging renders — often under $1 per image — with a growing selection of furniture styles and reliable quality for vacant room presentation.',
    lbStrength:
      'ListBright bundles virtual staging with AI photo enhancement, sky replacement, copywriting, multilingual support, a video, a flyer, and a shareable microsite — one platform, one upload, complete listing.',
    pricing: { lb: 'From $15 / listing (all-in)', them: '$0.50–$3/image (staging renders only)' },
    timeToList: { lb: '~10 min', them: 'Staged images only — no copy, video, or flyer' },
    rows: baseRows({
      'Virtual staging': { lb: 'yes', them: 'yes' },
    }),
    theirPros: [
      'Very affordable per-image pricing for vacant room staging',
      'Fast turnaround — staged renders in under a minute',
      'Simple interface with multiple style and furniture options',
    ],
    lbWins: [
      'All-in-one: staging, enhancement, and copy in a single workflow',
      'AI-written listing descriptions in 28+ languages included',
      'Slideshow video, PDF flyer, and shareable microsite auto-generated',
      'Purpose-built for real estate agents — not a general design tool',
    ],
    verdict:
      'Virtual Staging AI is a great budget option if all you need is vacant-room staging. ListBright is the better investment for agents who want a complete, publish-ready listing — because staging is just the starting point, not the finish line.',
  },

  {
    name: 'BrightShot',
    slug: 'brightshot',
    category: 'Real Estate Photo Editing',
    tagline: 'Professional real estate photo editing and retouching service',
    metaTitle: 'ListBright vs BrightShot – AI Listing Platform vs Photo Editing Service (2025)',
    metaDescription: 'Compare ListBright and BrightShot for real estate photo editing and listing production. AI speed and all-in-one workflow vs dedicated photo retouching.',
    heroTitle: 'ListBright vs BrightShot',
    heroDescription:
      'BrightShot focuses on polishing real estate photos with professional editing. ListBright goes further — AI-enhancing photos and then generating the copy, video, flyer, and every other asset the listing needs, all in one platform.',
    theirStrength:
      'BrightShot delivers professionally retouched property photos with careful attention to colour, brightness, and perspective — a reliable option for agents who want consistent, clean image output.',
    lbStrength:
      'ListBright uses AI to enhance photos instantly and then automatically produces listing descriptions in 28+ languages, a slideshow video, a branded PDF flyer, social media posts, and a shareable microsite from the same upload.',
    pricing: { lb: 'From $15 / listing (all-in)', them: 'Per-image pricing (editing only)' },
    timeToList: { lb: '~10 min', them: '12–24 hrs for edited photos only' },
    rows: baseRows({
      'AI photo enhancement': { lb: 'yes', them: 'partial' },
      'Sky & lawn replacement': { lb: 'yes', them: 'partial' },
      'Object & clutter removal': { lb: 'yes', them: 'partial' },
      'No special hardware needed': { lb: 'yes', them: 'yes' },
    }),
    theirPros: [
      'Consistent, professionally retouched photo output',
      'Reliable turnaround for photo editing',
      'Handles perspective correction and exposure balancing well',
    ],
    lbWins: [
      'AI delivers enhanced photos instantly — no waiting for human editing',
      'Generates listing copy, descriptions, and multilingual text automatically',
      'Produces video, PDF flyer, social posts, and microsite all from one upload',
      'All-in-one cost is often lower than buying photo editing separately',
    ],
    verdict:
      'BrightShot is a dependable photo editing service for agents who prioritise image quality and can wait for human turnaround. ListBright is the right choice for agents who need great photos plus a complete, publish-ready listing delivered in minutes — not hours.',
  },

  {
    name: 'Collov',
    slug: 'collov',
    category: 'AI Virtual Staging & Interior Design',
    tagline: 'AI-powered virtual staging and interior design visualization for residential and commercial spaces',
    metaTitle: 'ListBright vs Collov – Complete Listing Platform vs AI Virtual Staging (2025)',
    metaDescription: 'Compare ListBright and Collov for real estate AI tools. Full listing creation platform vs AI-powered virtual staging and interior design. Features, pricing, and verdict.',
    heroTitle: 'ListBright vs Collov',
    heroDescription:
      'Collov produces stunning AI-staged room renders in a wide range of interior styles. ListBright does that and everything else a listing needs — enhanced photos, listing copy in 28+ languages, video, flyer, and a shareable microsite.',
    theirStrength:
      'Collov delivers highly realistic AI virtual staging with an impressive range of interior design styles — from modern minimalist to classic luxury — making it one of the strongest staging-focused tools available for vacant room presentation.',
    lbStrength:
      'ListBright is a purpose-built real estate listing platform: virtual staging is one of many AI tools inside it. Upload photos, and ListBright enhances them, furnishes empty rooms, writes SEO-optimised descriptions in 28+ languages, and produces your video, flyer, and social posts automatically.',
    pricing: { lb: 'From $15 / listing (all-in)', them: 'Credits-based (~$29+/mo, staging only)' },
    timeToList: { lb: '~10 min', them: 'Staged images only — no listing copy, video, or flyer' },
    rows: baseRows({
      'Virtual staging': { lb: 'yes', them: 'yes' },
      'AI photo enhancement': { lb: 'yes', them: 'no' },
    }),
    theirPros: [
      'Exceptional AI staging quality with a wide range of design styles',
      'Strong results for both residential and commercial spaces',
      'Fast turnaround on staged renders with intuitive controls',
    ],
    lbWins: [
      'Staging plus full photo enhancement — HDR, sky, clutter — in one workflow',
      'AI listing descriptions in 28+ languages generated automatically',
      'Produces slideshow video, PDF flyer, social posts, and shareable microsite',
      'Purpose-built for real estate listing production, not just room visualization',
    ],
    verdict:
      'Collov is an excellent choice if stunning virtual staging is your primary need. ListBright is the right choice when you need the complete listing: staged and enhanced photos, persuasive multilingual copy, and every marketing asset — all produced from one upload in about 10 minutes.',
  },
];

export const allComparisonLinks = competitors.map(c => ({
  label: `vs ${c.name}`,
  href: `/compare/${c.slug}`,
  category: c.category,
}));
